const asArray = (value) => (Array.isArray(value) ? value : []);

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const parseAgeRange = (ageRange, ageMin, ageMax) => {
  if (ageMin !== undefined || ageMax !== undefined) {
    return { age_min: ageMin, age_max: ageMax };
  }

  const matches = String(ageRange || "").match(/\d+/g);
  if (!matches?.length) return {};
  if (matches.length === 1) {
    const age = Number(matches[0]);
    return { age_min: age, age_max: age };
  }
  return { age_min: Number(matches[0]), age_max: Number(matches[1]) };
};

const mapItinerary = (data) => {
  const itineraryDays = asArray(firstDefined(data?.itinerary_days, data?.itinerary));

  return itineraryDays.map((item, index) => ({
    day: firstDefined(
      item?.location,
      item?.day,
      `Day ${item?.day_number || index + 1}`
    ),
    altitude: firstDefined(item?.altitude, item?.altitude_band, "NA"),
    risk: firstDefined(item?.risk_description, "NA"),
    medical: firstDefined(item?.medical_facility, "NA"),
    evacuation: firstDefined(item?.evacuation_note, item?.evacuation, "NA"),
  }));
};

const mapBenefits = (data) => {
  if (Array.isArray(data?.benefit_descriptions)) return data.benefit_descriptions.filter(Boolean);

  return asArray(data?.cover_benefits)
    .map((item) => (typeof item === "string" ? item : firstDefined(item?.benefit_name, item?.benefit_description)))
    .filter(Boolean);
};

const mapFaqs = (data) => {
  if (Array.isArray(data?.faq)) {
    return data.faq.map((item) =>
      typeof item === "string"
        ? { question: item, answer: "Please contact support for detailed operational guidance." }
        : {
            question: firstDefined(item?.question, item?.title, "FAQ"),
            answer: firstDefined(item?.answer, "Please contact support for detailed operational guidance."),
          }
    );
  }

  return asArray(data?.cover_faqs).map((item) => ({
    question: firstDefined(item?.question, item?.title, "FAQ"),
    answer: firstDefined(item?.answer, "Please contact support for detailed operational guidance."),
  }));
};

const mapTerms = (data) => {
  if (Array.isArray(data?.terms_and_conditions)) return data.terms_and_conditions.filter(Boolean);

  return asArray(data?.cover_terms)
    .map((item) => (typeof item === "string" ? item : firstDefined(item?.text, item?.title)))
    .filter(Boolean);
};

const mapNotes = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : firstDefined(item?.text, item?.title, item?.note)))
      .filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const normalizePlanDetailData = (data) => {
  if (!data) return null;

  const travellingToNames = Array.isArray(data?.travelling_to_names)
    ? data.travelling_to_names
    : [data?.region, data?.access_point, data?.travelling_to_name].filter(Boolean);

  return {
    ...data,
    title: firstDefined(data?.cover_title, data?.title, data?.name, "Explore Plan"),
    subtitle: firstDefined(data?.subtitle),
    discount_label: firstDefined(data?.discount_label),
    promo_banner: firstDefined(data?.promo_banner),
    token: firstDefined(data?.token),
    cover_scope: firstDefined(data?.cover_scope, data?.region, "Global scope"),
    travelling_to_names: travellingToNames,
    max_altitude: firstDefined(data?.max_altitude, data?.altitude_limit),
    max_duration: firstDefined(
      data?.cover_max_duration,
      data?.max_duration,
      data?.duration_days,
      data?.duration
    ),
    min_duration: firstDefined(data?.cover_min_duration, data?.min_duration),
    type: firstDefined(data?.activity_type, data?.terrain, data?.type),
    difficulty: firstDefined(data?.difficulty),
    season_window: firstDefined(data?.season_window, data?.seasonWindow),
    altitude_band: firstDefined(data?.altitude_band),
    scope_description: firstDefined(data?.scope_description),
    region: firstDefined(data?.region),
    access_point: firstDefined(data?.access_point),
    terrain: firstDefined(data?.terrain),
    ...parseAgeRange(
      data?.age_range,
      firstDefined(data?.age_min, data?.cover_age_min),
      firstDefined(data?.age_max, data?.cover_age_max)
    ),
    start_date: firstDefined(data?.start_date),
    end_date: firstDefined(data?.end_date),
    brochure: firstDefined(
      data?.cover_brochure_url,
      data?.brochure,
      data?.promo_image,
      data?.image,
      data?.policy_wording
    ),
    route_map: firstDefined(
      data?.cover_image_url,
      data?.trek_map_image,
      data?.route_map,
      data?.route_map_image,
      data?.map_image,
      data?.coverage_map,
      data?.route_image,
      data?.route_chart
    ),
    benefit_descriptions: mapBenefits(data),
    itinerary: mapItinerary(data),
    faq: mapFaqs(data),
    pitch_text: firstDefined(data?.pitch_text),
    key_features: asArray(firstDefined(data?.key_features)),
    why_asc360_text: firstDefined(data?.why_asc360_text),
    why_asc360_bullets: asArray(firstDefined(data?.why_asc360_bullets)),
    stat_covered: firstDefined(data?.stat_covered),
    stat_evacuation: firstDefined(data?.stat_evacuation),
    stat_wilderness: firstDefined(data?.stat_wilderness),
    stat_settled_amount: firstDefined(data?.stat_settled_amount),
    comparison_rows: asArray(firstDefined(data?.comparison_rows, data?.competitor_comparisons)).map((row, rowIndex) => ({
      ...row,
      competitor_values: asArray(row?.competitor_values).map((item, index) => ({
        ...item,
        competitor_name: firstDefined(item?.competitor_name, item?.competitor_label, `Competitor ${index + 1}`),
      })),
      order: firstDefined(row?.order, rowIndex),
    })),
    terms_and_conditions: mapTerms(data),
    important_notes: mapNotes(firstDefined(data?.important_notes, data?.importantNotes)),
    conditional_coverage: mapNotes(firstDefined(data?.conditional_coverage, data?.conditional_notes, data?.conditionalNotes)),
    
  };
};